import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  AccountBox as AccountIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  RoomService as ServiceIcon,
  Lock as LockIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useStore } from '../store/useStore';
import Button from '../components/common/Button';

// Import Settings Tab components - New 5-tab structure
import { AccountManagementTab } from '../components/settings/AccountManagement';
import UserManagementTab from '../components/settings/UserManagement/UserManagementTab';
import SimplifiedBusinessHoursTab from '../components/settings/BusinessHours/SimplifiedBusinessHoursTab';
import SimplifiedServiceManagementTab from '../components/settings/ServiceManagement/SimplifiedServiceManagementTab';
import ExternalIntegrationDashboard from '../components/settings/ExternalIntegration/ExternalIntegrationDashboard';
import { DEFAULT_SETTINGS, SettingsAccess, ComprehensiveSettings } from '../types/settings';
import { SettingsUpdateFunction } from '../types/settings-update';
import { useSettingsPermissions } from '../hooks/usePermissions';
import { usePageTitle } from '../hooks/usePageTitle';
import { sanitizeText, sanitizeStoreName, sanitizeEmail, sanitizeAddress, sanitizePhoneNumber } from '../utils/sanitization';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `settings-tab-${index}`,
    'aria-controls': `settings-tabpanel-${index}`,
  };
}

// Settings内部コンポーネント（権限チェック付き）
const SettingsContent: React.FC = () => {
  usePageTitle('設定');
  // URLパラメータからタブインデックスを取得
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const initialTab = tabFromUrl ? parseInt(tabFromUrl, 10) : 0;
  
  const [value, setValue] = useState(Math.max(0, Math.min(4, initialTab))); // 0-4の範囲で制限
  const {
    user,
    services
  } = useStore();
  
  // 権限システム統合
  const {
    canManageUsers,
    canViewStoreSettings,
    canEditStoreSettings,
    currentRole,
  } = useSettingsPermissions();
  
  // 設定状態管理の実装
  const [settings, setSettings] = useState<ComprehensiveSettings>(DEFAULT_SETTINGS);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  
  // 権限ベースのSettingsAccess計算
  // 開発環境では全権限を有効化（Vite対応）
  const isDevelopment = import.meta.env.DEV;
  const settingsAccess: SettingsAccess = {
    canViewStore: isDevelopment ? true : canViewStoreSettings,
    canEditStore: isDevelopment ? true : canEditStoreSettings,
    canManageUsers: isDevelopment ? true : canManageUsers,
    canConfigureIntegrations: isDevelopment ? true : currentRole === 'owner',
    canAccessDataSettings: isDevelopment ? true : currentRole === 'owner',
    canModifyAISettings: isDevelopment ? true : currentRole === 'owner',
    canViewSystemSettings: isDevelopment ? true : canViewStoreSettings,
    canEditSystemSettings: isDevelopment ? true : canEditStoreSettings
  };
  
  // 設定の読み込み実装
  const loadSettings = async (): Promise<void> => {
    setSettingsLoading(true);
    setSettingsError(null);
    
    try {
      // LocalStorageから設定を読み込み（将来的にはAPI/Supabase連携）
      const storedSettings = localStorage.getItem('salonhub-settings');
      
      if (storedSettings) {
        const parsedSettings = JSON.parse(storedSettings);
        // デフォルト設定とマージして不足フィールドを補完
        const mergedSettings = {
          ...DEFAULT_SETTINGS,
          ...parsedSettings,
          lastUpdated: new Date(parsedSettings.lastUpdated || new Date()),
        };
        setSettings(mergedSettings);
      } else {
        // 初期設定をLocalStorageに保存
        saveSettings(DEFAULT_SETTINGS);
      }
    } catch (error) {
      setSettingsError('設定の読み込みに失敗しました');
      // エラー時はデフォルト設定を使用
      setSettings(DEFAULT_SETTINGS);
    } finally {
      setSettingsLoading(false);
    }
  };
  
  // 設定の保存実装
  const saveSettings = async (newSettings: ComprehensiveSettings): Promise<void> => {
    try {
      const settingsToSave = {
        ...newSettings,
        lastUpdated: new Date(),
        updatedBy: user?.id || 'unknown',
      };
      
      localStorage.setItem('salonhub-settings', JSON.stringify(settingsToSave));
      setSettings(settingsToSave);
      
      setSnackbarMessage('設定が保存されました');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage('設定の保存に失敗しました');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };
  
  // Settings更新用ヘルパー関数（セキュリティ強化版）
  const updateSettings: SettingsUpdateFunction = async (section, updates) => {
    // 入力サニタイゼーション処理
    const sanitizedUpdates = sanitizeSettingsInput(section, updates);

    // 型安全にセクションデータを取得
    const sectionData = settings[section] || {};

    const newSettings = {
      ...settings,
      [section]: {
        ...sectionData,
        ...sanitizedUpdates,
      },
    } as ComprehensiveSettings;
    await saveSettings(newSettings);
  };
  
  // 設定項目別サニタイゼーション
  const sanitizeSettingsInput = <K extends keyof ComprehensiveSettings>(
    section: K,
    updates: Partial<ComprehensiveSettings[K]>
  ): Partial<ComprehensiveSettings[K]> => {
    const sanitized: Record<string, unknown> = updates && typeof updates === 'object' ? { ...updates } : {};
    
    switch (section) {
      case 'store':
        if ('storeName' in sanitized && typeof sanitized.storeName === 'string') {
          sanitized.storeName = sanitizeStoreName(sanitized.storeName);
        }
        if ('contactInfo' in sanitized && sanitized.contactInfo && typeof sanitized.contactInfo === 'object') {
          const contactInfo = sanitized.contactInfo as Record<string, unknown>;
          if (contactInfo.email && typeof contactInfo.email === 'string') {
            contactInfo.email = sanitizeEmail(contactInfo.email);
          }
          if (contactInfo.phone && typeof contactInfo.phone === 'string') {
            contactInfo.phone = sanitizePhoneNumber(contactInfo.phone);
          }
          sanitized.contactInfo = contactInfo;
        }
        if (sanitized.address && typeof sanitized.address === 'object') {
          const address = sanitized.address as Record<string, unknown>;
          if (address.address1 && typeof address.address1 === 'string') {
            address.address1 = sanitizeAddress(address.address1);
          }
          if (address.address2 && typeof address.address2 === 'string') {
            address.address2 = sanitizeAddress(address.address2);
          }
          if (address.building && typeof address.building === 'string') {
            address.building = sanitizeAddress(address.building);
          }
          sanitized.address = address;
        }
        break;
        
      case 'users':
        // ユーザー管理データのサニタイゼーション
        if ('staffAccounts' in sanitized && Array.isArray(sanitized.staffAccounts)) {
          sanitized.staffAccounts = sanitized.staffAccounts.map((staff) => {
            const staffObj = staff as Record<string, unknown>;
            return {
              ...staffObj,
              name: staffObj.name && typeof staffObj.name === 'string' ? sanitizeText(staffObj.name, 100) : staffObj.name,
              email: staffObj.email && typeof staffObj.email === 'string' ? sanitizeEmail(staffObj.email) : staffObj.email
            };
          });
        }
        break;
        
      default:
        // その他のテキストフィールドを一般的にサニタイゼーション
        Object.keys(sanitized).forEach(key => {
          if (typeof sanitized[key] === 'string') {
            sanitized[key] = sanitizeText(sanitized[key], 500);
          }
        });
    }
    
    return sanitized as Partial<ComprehensiveSettings[K]>;
  };
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Business hours settings are now managed by SimplifiedBusinessHoursTab
  
  // 拡張設定用状態
  // const [localSettings, setLocalSettings] = useState(settings);
  
  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');


  // 初期化とデータ同期
  useEffect(() => {
    // Settings初期読み込み
    const initializeSettings = async () => {
      try {
        await loadSettings();
      } catch (error) {
        setSnackbarMessage('設定の読み込みに失敗しました');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    };

    initializeSettings();
  }, []); // 依存関係を空にして初回のみ実行

  // Settings読み込み後の処理
  useEffect(() => {
    if (settings && !settingsLoading) {
      // 設定が読み込まれた後の処理（本番環境では無効化）
    }
  }, [settings, settingsLoading, user]);

  // 権限チェック
  useEffect(() => {
    // 権限チェック完了（本番環境では詳細ログを無効化）
  }, [user?.role, currentRole]); // getSettingsAccessは再作成されるため直接依存を指定

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  // Business hours are now handled by SimplifiedBusinessHoursTab

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // エラー表示
  if (settingsError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          設定の読み込みでエラーが発生しました: {settingsError}
        </Alert>
        <Button variant="outlined" onClick={loadSettings}>
          再試行
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ mb: 3, fontWeight: 'bold', color: 'text.primary' }}>
        設定
      </Typography>

<Paper sx={{ width: '100%' }}>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="設定タブ"
          variant={isMobile || isTablet ? "scrollable" : "standard"}
          scrollButtons={isMobile || isTablet ? "auto" : false}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: isMobile ? 56 : 64,
              textTransform: 'none',
              fontSize: isMobile ? '0.75rem' : '0.875rem',
              fontWeight: 500,
              px: isMobile ? 1 : 2,
            },
            '& .MuiTab-iconWrapper': {
              marginRight: isMobile ? 0.5 : 1,
            },
            '& .Mui-disabled': {
              color: 'text.disabled',
              opacity: 0.6,
            },
          }}
        >
          {/* Account Management Tab - Always accessible */}
          <Tab 
            label={isMobile ? "アカウント" : "アカウント管理"} 
            icon={<AccountIcon />} 
            iconPosition="start"
            {...a11yProps(0)} 
          />
          
          {/* User Management Tab - With permission control */}
          {(isDevelopment || settingsAccess.canManageUsers) ? (
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {isMobile ? "ユーザー" : "ユーザー管理"}
                </Box>
              } 
              icon={<PersonIcon />} 
              iconPosition="start"
              disabled={false}
              {...a11yProps(1)} 
            />
          ) : (
            <Tooltip 
              title={`ユーザー管理の権限がありません（現在の役職: ${currentRole}）`}
              arrow
              placement="top"
            >
              <span>
                <Tab 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {isMobile ? "ユーザー" : "ユーザー管理"}
                      <LockIcon sx={{ fontSize: 16, opacity: 0.6 }} />
                    </Box>
                  } 
                  icon={<PersonIcon />} 
                  iconPosition="start"
                  disabled={true}
                  {...a11yProps(1)} 
                />
              </span>
            </Tooltip>
          )}
          
          {/* Business Hours Tab - With permission control */}
          {(isDevelopment || settingsAccess.canEditStore) ? (
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {isMobile ? "営業時間" : "営業時間設定"}
                </Box>
              } 
              icon={<TimeIcon />} 
              iconPosition="start"
              disabled={false}
              {...a11yProps(2)} 
            />
          ) : (
            <Tooltip 
              title={`営業時間設定の編集権限がありません（現在の役職: ${currentRole}）`}
              arrow
              placement="top"
            >
              <span>
                <Tab 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {isMobile ? "営業時間" : "営業時間設定"}
                      <LockIcon sx={{ fontSize: 16, opacity: 0.6 }} />
                    </Box>
                  } 
                  icon={<TimeIcon />} 
                  iconPosition="start"
                  disabled={true}
                  {...a11yProps(2)} 
                />
              </span>
            </Tooltip>
          )}
          
          {/* Service Management Tab - With permission control */}
          {(isDevelopment || settingsAccess.canEditStore) ? (
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {isMobile ? "サービス" : "サービス管理"}
                </Box>
              } 
              icon={<ServiceIcon />} 
              iconPosition="start"
              disabled={false}
              {...a11yProps(3)} 
            />
          ) : (
            <Tooltip 
              title={`サービス管理の編集権限がありません（現在の役職: ${currentRole}）`}
              arrow
              placement="top"
            >
              <span>
                <Tab 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {isMobile ? "サービス" : "サービス管理"}
                      <LockIcon sx={{ fontSize: 16, opacity: 0.6 }} />
                    </Box>
                  } 
                  icon={<ServiceIcon />} 
                  iconPosition="start"
                  disabled={true}
                  {...a11yProps(3)} 
                />
              </span>
            </Tooltip>
          )}
          
          {/* External Integration Tab - With permission control */}
          {(isDevelopment || settingsAccess.canConfigureIntegrations) ? (
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {isMobile ? "外部連携" : "外部サイト連携"}
                </Box>
              } 
              icon={<SyncIcon />} 
              iconPosition="start"
              disabled={false}
              {...a11yProps(4)} 
            />
          ) : (
            <Tooltip 
              title={`外部サイト連携の設定権限がありません（現在の役職: ${currentRole}）`}
              arrow
              placement="top"
            >
              <span>
                <Tab 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {isMobile ? "外部連携" : "外部サイト連携"}
                      <LockIcon sx={{ fontSize: 16, opacity: 0.6 }} />
                    </Box>
                  } 
                  icon={<SyncIcon />} 
                  iconPosition="start"
                  disabled={true}
                  {...a11yProps(4)} 
                />
              </span>
            </Tooltip>
          )}
        </Tabs>

        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Tab 0: Account Management (SalonHub billing) */}
          <TabPanel value={value} index={0}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <AccountManagementTab
                settings={settings}
                updateSettings={updateSettings}
                onError={(error) => {
                  setSnackbarMessage(error);
                  setSnackbarSeverity('error');
                  setSnackbarOpen(true);
                }}
                onSuccess={(message) => {
                  setSnackbarMessage(message);
                  setSnackbarSeverity('success');
                  setSnackbarOpen(true);
                }}
              />
            </motion.div>
          </TabPanel>

          {/* Tab 1: User Management */}
          <TabPanel value={value} index={1}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {settingsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                  <CircularProgress size={24} />
                  <Typography sx={{ ml: 2 }}>ユーザー管理を読み込み中...</Typography>
                </Box>
              ) : (
                <UserManagementTab 
                  settings={settings}
                  updateSettings={updateSettings}
                />
              )}
              
              {!settingsAccess.canManageUsers && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  ユーザー管理の権限がありません
                </Alert>
              )}
            </motion.div>
          </TabPanel>

          {/* Tab 2: Enhanced Business Hours */}
          <TabPanel value={value} index={2}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {settingsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                  <CircularProgress size={24} />
                  <Typography sx={{ ml: 2 }}>営業時間設定を読み込み中...</Typography>
                </Box>
              ) : settingsAccess.canEditStore ? (
                <SimplifiedBusinessHoursTab
                  settings={settings as unknown as Record<string, unknown>}
                  updateSettings={(section: string, updates: any) =>
                    updateSettings(section as keyof ComprehensiveSettings, updates)
                  }
                />
              ) : (
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                    営業時間設定
                  </Typography>
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    営業時間設定の編集権限がありません。閲覧のみ可能です。
                  </Alert>
                </Box>
              )}
            </motion.div>
          </TabPanel>

          {/* Tab 3: Service Management */}
          <TabPanel value={value} index={3}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {settingsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                  <CircularProgress size={24} />
                  <Typography sx={{ ml: 2 }}>サービス管理を読み込み中...</Typography>
                </Box>
              ) : settingsAccess.canEditStore ? (
                <SimplifiedServiceManagementTab
                  services={services?.map(s => ({
                    ...s,
                    type: s.type || 'normal' as const,
                    isActive: s.isActive !== undefined ? s.isActive : true,
                    description: s.description || '',
                    categoryId: s.categoryId || s.category || 'other',
                    searchCategoryId: s.searchCategoryId || s.categoryId || s.category || 'other',
                    priceDisplay: (s.priceDisplay || 'normal') as 'normal' | 'range' | 'ask',
                    couponType: s.couponType as 'all' | 'new' | 'repeat' | undefined,
                    menuDesignation: s.menuDesignation as 'with' | 'without' | undefined,
                    discountType: s.discountType as 'amount' | 'percent' | undefined
                  })) || []}
                  updateSettings={(section: string, updates: any) =>
                    updateSettings(section as keyof ComprehensiveSettings, updates)
                  }
                />
              ) : (
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                    サービス・メニュー管理
                  </Typography>
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    サービス管理の編集権限がありません。閲覧のみ可能です。
                  </Alert>
                </Box>
              )}
            </motion.div>
          </TabPanel>

          {/* Tab 4: External Integration */}
          <TabPanel value={value} index={4}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {settingsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                  <CircularProgress size={24} />
                  <Typography sx={{ ml: 2 }}>外部サイト連携を読み込み中...</Typography>
                </Box>
              ) : settingsAccess.canConfigureIntegrations ? (
                <ExternalIntegrationDashboard
                  storeId={user?.id || 'demo-store'}
                  initialTab={0}
                  onTabChange={(tab) => console.log('External integration tab changed:', tab)}
                />
              ) : (
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                    外部予約サイト連携
                  </Typography>
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    外部サイト連携の設定権限がありません。閲覧のみ可能です。
                  </Alert>
                </Box>
              )}
            </motion.div>
          </TabPanel>
        </Box>
      </Paper>
      
      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity} 
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// メインのSettingsコンポーネント（グローバルPermissionProviderを使用）
const Settings: React.FC = () => {
  return <SettingsContent />;
};

export default Settings;
